import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Target, Users, DollarSign, Activity, PieChart, LineChart, Zap, Crown } from 'lucide-react';
import { database } from '../../Config/firebase';
import { ref, onValue } from '../../Config/matuRtdb';

const AdvancedAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    userStats: {
      totalBets: 0,
      totalWins: 0,
      totalLosses: 0,
      winRate: 0,
      totalProfit: 0,
      averageBet: 0,
      bestStreak: 0,
      currentStreak: 0
    },
    performance: {
      daily: [],
      weekly: [],
      monthly: []
    },
    sports: {
      football: { bets: 0, wins: 0, profit: 0 },
      basketball: { bets: 0, wins: 0, profit: 0 },
      tennis: { bets: 0, wins: 0, profit: 0 }
    },
    predictions: {
      accuracy: 0,
      totalPredictions: 0,
      successfulPredictions: 0
    }
  });

  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedSport, setSelectedSport] = useState('all');

  // Datos de ejemplo para gráficos
  const sampleData = {
    daily: [
      { date: '2024-02-01', bets: 5, wins: 3, profit: 1500 },
      { date: '2024-02-02', bets: 8, wins: 6, profit: 3200 },
      { date: '2024-02-03', bets: 3, wins: 2, profit: 800 },
      { date: '2024-02-04', bets: 12, wins: 9, profit: 4500 },
      { date: '2024-02-05', bets: 6, wins: 4, profit: 1800 },
      { date: '2024-02-06', bets: 9, wins: 7, profit: 2800 },
      { date: '2024-02-07', bets: 7, wins: 5, profit: 2200 }
    ],
    weekly: [
      { week: 'Semana 1', bets: 45, wins: 32, profit: 12500 },
      { week: 'Semana 2', bets: 52, wins: 38, profit: 15800 },
      { week: 'Semana 3', bets: 38, wins: 25, profit: 8900 },
      { week: 'Semana 4', bets: 61, wins: 44, profit: 19200 }
    ],
    monthly: [
      { month: 'Enero', bets: 180, wins: 125, profit: 48500 },
      { month: 'Febrero', bets: 165, wins: 118, profit: 44200 }
    ]
  };

  useEffect(() => {
    // Simular carga de datos
    setAnalytics(prev => ({
      ...prev,
      userStats: {
        totalBets: 345,
        totalWins: 243,
        totalLosses: 102,
        winRate: 70.4,
        totalProfit: 92700,
        averageBet: 268,
        bestStreak: 12,
        currentStreak: 5
      },
      performance: sampleData,
      sports: {
        football: { bets: 180, wins: 135, profit: 52000 },
        basketball: { bets: 95, wins: 68, profit: 28000 },
        tennis: { bets: 70, wins: 40, profit: 12700 }
      },
      predictions: {
        accuracy: 78.5,
        totalPredictions: 200,
        successfulPredictions: 157
      }
    }));
  }, []);

  const getCurrentData = () => {
    switch (selectedPeriod) {
      case 'day': return analytics.performance.daily;
      case 'week': return analytics.performance.weekly;
      case 'month': return analytics.performance.monthly;
      default: return analytics.performance.weekly;
    }
  };

  const getSportData = () => {
    if (selectedSport === 'all') {
      return Object.entries(analytics.sports).map(([sport, data]) => ({
        sport,
        ...data,
        winRate: ((data.wins / data.bets) * 100).toFixed(1)
      }));
    }
    return [{
      sport: selectedSport,
      ...analytics.sports[selectedSport],
      winRate: ((analytics.sports[selectedSport].wins / analytics.sports[selectedSport].bets) * 100).toFixed(1)
    }];
  };

  const getProfitColor = (profit) => {
    return profit >= 0 ? 'text-green-400' : 'text-red-400';
  };

  const getWinRateColor = (rate) => {
    if (rate >= 70) return 'text-green-400';
    if (rate >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BarChart3 className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Análisis Avanzado
            </h1>
            <Activity className="w-8 h-8 text-purple-400" />
          </div>
          <p className="text-gray-300 text-lg">
            Estadísticas detalladas y análisis de rendimiento
          </p>
        </div>

        {/* Stats principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-800/50 backdrop-blur-sm rounded-xl p-4 border border-blue-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-gray-300">Apuestas</span>
            </div>
            <div className="text-2xl font-bold text-blue-400">{analytics.userStats.totalBets}</div>
            <div className="text-xs text-gray-400 mt-1">
              {analytics.userStats.winRate}% de éxito
            </div>
          </div>
          
          <div className="bg-green-800/50 backdrop-blur-sm rounded-xl p-4 border border-green-600/30">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-300">Ganancias</span>
            </div>
            <div className={`text-2xl font-bold ${getProfitColor(analytics.userStats.totalProfit)}`}>
              ${analytics.userStats.totalProfit.toLocaleString()}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              ${analytics.userStats.averageBet} promedio
            </div>
          </div>
          
          <div className="bg-purple-800/50 backdrop-blur-sm rounded-xl p-4 border border-purple-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-gray-300">Racha</span>
            </div>
            <div className="text-2xl font-bold text-purple-400">{analytics.userStats.currentStreak}</div>
            <div className="text-xs text-gray-400 mt-1">
              Mejor: {analytics.userStats.bestStreak}
            </div>
          </div>
          
          <div className="bg-yellow-800/50 backdrop-blur-sm rounded-xl p-4 border border-yellow-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              <span className="text-sm text-gray-300">Precisión</span>
            </div>
            <div className="text-2xl font-bold text-yellow-400">{analytics.predictions.accuracy}%</div>
            <div className="text-xs text-gray-400 mt-1">
              {analytics.predictions.successfulPredictions}/{analytics.predictions.totalPredictions}
            </div>
          </div>
        </div>

        {/* Controles de filtros */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 mb-8 border border-gray-700/50">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Rendimiento por Período</h2>
            <div className="flex items-center gap-4">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-4 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="day">Diario</option>
                <option value="week">Semanal</option>
                <option value="month">Mensual</option>
              </select>
              
              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="px-4 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos los deportes</option>
                <option value="football">⚽ Fútbol</option>
                <option value="basketball">🏀 Baloncesto</option>
                <option value="tennis">🎾 Tenis</option>
              </select>
            </div>
          </div>

          {/* Gráfico de rendimiento */}
          <div className="space-y-4">
            {getCurrentData().map((period, index) => (
              <div key={index} className="bg-gray-700/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">
                    {period.date || period.week || period.month}
                  </h3>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-400">
                      {period.bets} apuestas
                    </span>
                    <span className="text-green-400">
                      {period.wins} victorias
                    </span>
                    <span className={`font-bold ${getProfitColor(period.profit)}`}>
                      ${period.profit.toLocaleString()}
                    </span>
                  </div>
                </div>
                
                {/* Barra de progreso */}
                <div className="w-full bg-gray-600 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all"
                    style={{ width: `${(period.wins / period.bets) * 100}%` }}
                  />
                </div>
                
                <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                  <span>Win Rate: {((period.wins / period.bets) * 100).toFixed(1)}%</span>
                  <span>Promedio: ${Math.round(period.profit / period.bets)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Análisis por deporte */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-400" />
              Rendimiento por Deporte
            </h2>
            
            <div className="space-y-4">
              {getSportData().map((sportData, index) => (
                <div key={index} className="bg-gray-700/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {sportData.sport === 'football' ? '⚽' : 
                         sportData.sport === 'basketball' ? '🏀' : '🎾'}
                      </span>
                      <div>
                        <h3 className="font-semibold capitalize">{sportData.sport}</h3>
                        <p className="text-sm text-gray-400">
                          {sportData.bets} apuestas totales
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${getWinRateColor(sportData.winRate)}`}>
                        {sportData.winRate}%
                      </div>
                      <div className={`text-sm ${getProfitColor(sportData.profit)}`}>
                        ${sportData.profit.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${sportData.winRate}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <LineChart className="w-5 h-5 text-purple-400" />
              Tendencias de Ganancias
            </h2>
            
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-green-600/20 to-blue-600/20 rounded-lg p-4 border border-green-500/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-300">Tendencia Semanal</span>
                  <span className="text-green-400 font-bold">+15.2%</span>
                </div>
                <div className="text-2xl font-bold text-green-400">
                  ${analytics.userStats.totalProfit.toLocaleString()}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Crecimiento constante en las últimas 4 semanas
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg p-4 border border-blue-500/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-300">Win Rate Promedio</span>
                  <span className="text-blue-400 font-bold">{analytics.userStats.winRate}%</span>
                </div>
                <div className="text-2xl font-bold text-blue-400">
                  {analytics.userStats.totalWins}/{analytics.userStats.totalBets}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {analytics.userStats.totalWins} victorias de {analytics.userStats.totalBets} apuestas
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg p-4 border border-purple-500/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-300">Apuesta Promedio</span>
                  <span className="text-purple-400 font-bold">${analytics.userStats.averageBet}</span>
                </div>
                <div className="text-2xl font-bold text-purple-400">
                  ${analytics.userStats.totalProfit.toLocaleString()}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Ganancia total con apuestas moderadas
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Insights y recomendaciones */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            <Activity className="w-5 h-5 text-yellow-400" />
            Insights y Recomendaciones
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-800/20 rounded-lg p-4 border border-green-500/30">
              <h3 className="font-semibold text-green-400 mb-2">💡 Mejor Deporte</h3>
              <p className="text-sm text-gray-300">
                El fútbol es tu deporte más rentable con un 75% de éxito. 
                Considera aumentar tus apuestas en este deporte.
              </p>
            </div>
            
            <div className="bg-blue-800/20 rounded-lg p-4 border border-blue-500/30">
              <h3 className="font-semibold text-blue-400 mb-2">📈 Tendencia Positiva</h3>
              <p className="text-sm text-gray-300">
                Tu win rate ha mejorado un 12% en el último mes. 
                Mantén esta estrategia de apuestas.
              </p>
            </div>
            
            <div className="bg-yellow-800/20 rounded-lg p-4 border border-yellow-500/30">
              <h3 className="font-semibold text-yellow-400 mb-2">🎯 Optimización</h3>
              <p className="text-sm text-gray-300">
                Considera reducir apuestas en tenis (57% win rate) 
                y enfócate más en fútbol y baloncesto.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalytics; 